import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  MessageCircle,
  Mail,
  ChevronDown,
  Book,
  CreditCard,
  Users,
  Settings,
  Zap,
  HelpCircle,
} from 'lucide-react';
import StaticPageLayout from '../../components/StaticPageLayout';
import { useThemeStore } from '../../store/theme';

const categories = [
  {
    icon: Zap,
    title: 'Getting Started',
    questions: [
      {
        q: 'How do I create an account?',
        a: 'Click the "Get Started" button on our homepage and follow the registration process. You can sign up with your email or use Google/GitHub authentication.',
      },
      {
        q: 'What AI employees are available?',
        a: 'We offer 15 specialized AI employees including Aria (Social Media), Nova (Customer Support), Atlas (Data Analysis), and more. Each has unique capabilities for different business needs.',
      },
      {
        q: 'How do credits work?',
        a: 'Credits are used each time you interact with an AI employee. Simple messages use 1 credit, while complex tasks may use more. You can view your credit balance in your dashboard.',
      },
    ],
  },
  {
    icon: CreditCard,
    title: 'Billing & Subscription',
    questions: [
      {
        q: 'What plans are available?',
        a: 'We offer Free (100 credits/month), Pro ($25/month, 1,500 credits), Professional ($50/month, 3,000 credits), and Enterprise ($150/month, 10,000 credits) plans.',
      },
      {
        q: 'Can I change my plan?',
        a: "Yes, you can upgrade or downgrade anytime from your Settings page. Changes take effect immediately for upgrades, or at the next billing cycle for downgrades.",
      },
      {
        q: 'What payment methods do you accept?',
        a: 'We accept all major credit cards (Visa, Mastercard, American Express), PayPal, and bank transfers for Enterprise plans.',
      },
      {
        q: 'Do you offer refunds?',
        a: 'Yes, we offer a 14-day money-back guarantee on all paid plans. Contact support within 14 days of purchase for a full refund.',
      },
    ],
  },
  {
    icon: Users,
    title: 'AI Employees',
    questions: [
      {
        q: 'Can I customize AI employee responses?',
        a: 'Yes! Use Brain AI to upload your brand guidelines, product information, and FAQs. AI employees will use this knowledge in their responses.',
      },
      {
        q: 'How accurate are the AI employees?',
        a: 'Our AI employees are powered by state-of-the-art language models and are highly accurate. For critical tasks, we recommend reviewing outputs before publishing.',
      },
      {
        q: 'Can AI employees access external websites?',
        a: 'AI employees can analyze content you provide but cannot browse the internet in real-time. You can paste content or upload files for analysis.',
      },
    ],
  },
  {
    icon: Settings,
    title: 'Technical Issues',
    questions: [
      {
        q: 'Why is my response taking too long?',
        a: 'Complex tasks may take longer to process. If responses consistently take over 30 seconds, try refreshing the page or checking your internet connection.',
      },
      {
        q: 'How do I report a bug?',
        a: 'You can report bugs through our Help Center chat, email support@flowai.com, or use the feedback button in the dashboard.',
      },
      {
        q: 'Is there an API rate limit?',
        a: 'Yes, rate limits vary by plan: Free (10/min), Pro (60/min), Professional (300/min), Enterprise (custom). See our API docs for details.',
      },
    ],
  },
];

export default function HelpCenter() {
  const [searchQuery, setSearchQuery] = useState('');
  const [openCategory, setOpenCategory] = useState<string | null>('Getting Started');
  const [openQuestion, setOpenQuestion] = useState<string | null>(null);
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  const filteredCategories = categories.map((cat) => ({
    ...cat,
    questions: cat.questions.filter(
      (q) =>
        q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
        q.a.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  })).filter((cat) => cat.questions.length > 0);

  return (
    <StaticPageLayout
      title="Help Center"
      subtitle="Find answers to common questions or get in touch with our support team."
    >
      {/* Search */}
      <section className="mb-12">
        <div className="relative max-w-2xl mx-auto">
          <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-500' : 'text-slate-400'}`} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search for answers..."
            className={`w-full pl-12 pr-4 py-4 rounded-2xl text-lg focus:outline-none transition-colors ${
              isDark
                ? 'bg-dark-800 border border-white/10 text-white placeholder-gray-500 focus:border-purple-500'
                : 'bg-white border border-slate-200 text-slate-900 placeholder-slate-400 focus:border-purple-500 shadow-sm'
            }`}
          />
        </div>
      </section>

      {/* Quick Actions */}
      <section className="mb-12">
        <div className="grid md:grid-cols-3 gap-6">
          <a
            href="mailto:support@flowai.com"
            className={`flex items-center gap-4 p-6 rounded-2xl border transition-all group ${
              isDark
                ? 'bg-dark-800/50 border-white/5 hover:border-purple-500/30'
                : 'bg-white border-slate-200 hover:border-purple-300 shadow-sm'
            }`}
          >
            <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
              <Mail className={`w-6 h-6 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
            </div>
            <div>
              <h3 className={`text-lg font-semibold transition-colors ${
                isDark ? 'text-white group-hover:text-purple-300' : 'text-slate-900 group-hover:text-purple-600'
              }`}>
                Email Support
              </h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Get help via email</p>
            </div>
          </a>
          <a
            href="#chat"
            className={`flex items-center gap-4 p-6 rounded-2xl border transition-all group ${
              isDark
                ? 'bg-dark-800/50 border-white/5 hover:border-purple-500/30'
                : 'bg-white border-slate-200 hover:border-cyan-300 shadow-sm'
            }`}
          >
            <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center">
              <MessageCircle className={`w-6 h-6 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />
            </div>
            <div>
              <h3 className={`text-lg font-semibold transition-colors ${
                isDark ? 'text-white group-hover:text-cyan-300' : 'text-slate-900 group-hover:text-cyan-600'
              }`}>
                Live Chat
              </h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Chat with support</p>
            </div>
          </a>
          <a
            href="/docs"
            className={`flex items-center gap-4 p-6 rounded-2xl border transition-all group ${
              isDark
                ? 'bg-dark-800/50 border-white/5 hover:border-purple-500/30'
                : 'bg-white border-slate-200 hover:border-green-300 shadow-sm'
            }`}
          >
            <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
              <Book className={`w-6 h-6 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
            </div>
            <div>
              <h3 className={`text-lg font-semibold transition-colors ${
                isDark ? 'text-white group-hover:text-green-300' : 'text-slate-900 group-hover:text-green-600'
              }`}>
                Documentation
              </h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Browse the docs</p>
            </div>
          </a>
        </div>
      </section>

      {/* FAQ Categories */}
      <section className="mb-16">
        <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>Frequently Asked Questions</h2>
        <div className="space-y-4">
          {(searchQuery ? filteredCategories : categories).map((category) => (
            <div
              key={category.title}
              className={`rounded-2xl border overflow-hidden ${
                isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'
              }`}
            >
              <button
                onClick={() =>
                  setOpenCategory(openCategory === category.title ? null : category.title)
                }
                className="w-full flex items-center justify-between p-6 text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                    <category.icon className={`w-5 h-5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                  </div>
                  <span className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{category.title}</span>
                  <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                    {category.questions.length} questions
                  </span>
                </div>
                <ChevronDown
                  className={`w-5 h-5 transition-transform ${isDark ? 'text-gray-400' : 'text-slate-400'} ${
                    openCategory === category.title ? 'rotate-180' : ''
                  }`}
                />
              </button>

              <AnimatePresence>
                {openCategory === category.title && (
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: 'auto' }}
                    exit={{ height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 space-y-3">
                      {category.questions.map((item) => (
                        <div
                          key={item.q}
                          className={`rounded-xl border ${
                            isDark ? 'bg-dark-700/50 border-white/5' : 'bg-slate-50 border-slate-100'
                          }`}
                        >
                          <button
                            onClick={() =>
                              setOpenQuestion(openQuestion === item.q ? null : item.q)
                            }
                            className="w-full flex items-center justify-between p-4 text-left"
                          >
                            <div className="flex items-center gap-3">
                              <HelpCircle className={`w-5 h-5 ${isDark ? 'text-gray-500' : 'text-slate-400'}`} />
                              <span className={isDark ? 'text-white' : 'text-slate-900'}>{item.q}</span>
                            </div>
                            <ChevronDown
                              className={`w-4 h-4 transition-transform ${isDark ? 'text-gray-500' : 'text-slate-400'} ${
                                openQuestion === item.q ? 'rotate-180' : ''
                              }`}
                            />
                          </button>
                          <AnimatePresence>
                            {openQuestion === item.q && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden"
                              >
                                <div className={`px-4 pb-4 pl-12 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>{item.a}</div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className={`text-center p-8 rounded-2xl border ${
        isDark
          ? 'bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border-white/5'
          : 'bg-gradient-to-br from-purple-50 to-cyan-50 border-purple-100'
      }`}>
        <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Still Need Help?</h2>
        <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
          Our support team typically responds within 2 hours during business hours.
        </p>
        <a
          href="mailto:support@flowai.com"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl font-medium text-white shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow"
        >
          <Mail className="w-5 h-5" />
          Contact Support
        </a>
      </section>
    </StaticPageLayout>
  );
}
